// texe-web/src/hooks/usePostInteractions.ts
import { useState, useCallback } from 'react';
import { apiPost, apiDelete } from '../lib/api';
import { withRetry, RetryPresets } from '../lib/retryUtils';
import { useRateLimitMessage } from './useRateLimitMessage';

/**
 * Merkezi Post Etkileşim Hook'u - Web Version
 * - Like/Unlike with optimistic updates
 * - Comment sayısı takibi
 * - Batch loading for performance
 * - Enhanced error handling with retry
 * - Loading states for better UX
 */

export interface PostInteractionState {
  likeCount: number;
  commentCount: number;
  liked: boolean;
  loading?: boolean;
  error?: string | null;
}

export interface UsePostInteractionsReturn {
  // State
  interactions: Record<string, PostInteractionState>;
  
  // Actions
  toggleLike: (postId: string) => Promise<void>;
  updateLikeCount: (postId: string, count: number, liked: boolean) => void;
  updateCommentCount: (postId: string, count: number) => void;
  setInteraction: (postId: string, state: Partial<PostInteractionState>) => void;
  batchLoadInteractions: (postIds: string[]) => Promise<void>;
  
  // Helpers
  getInteraction: (postId: string) => PostInteractionState;
  clearErrors: (postId?: string) => void;
}

export function usePostInteractions(): UsePostInteractionsReturn {
  const [interactions, setInteractions] = useState<Record<string, PostInteractionState>>({});
  const { formatRateLimitError, isRateLimitError } = useRateLimitMessage();

  /**
   * Tek bir post'un etkileşim durumunu al
   * Returns a new object every time to trigger React re-renders
   */
  const getInteraction = useCallback((postId: string): PostInteractionState => {
    const existing = interactions[postId];
    // Return a NEW object every time to ensure React detects changes
    return {
      likeCount: existing?.likeCount || 0,
      commentCount: existing?.commentCount || 0,
      liked: existing?.liked || false,
      loading: existing?.loading || false,
      error: existing?.error || null,
    };
  }, [interactions]);

  /**
   * Post etkileşim durumunu güncelle (partial update)
   */
  const setInteraction = useCallback((postId: string, state: Partial<PostInteractionState>) => {
    setInteractions(prev => ({
      ...prev,
      [postId]: {
        ...prev[postId],
        ...state,
      },
    }));
  }, []);

  /**
   * Like sayısını manuel güncelle (realtime events için)
   */
  const updateLikeCount = useCallback((postId: string, count: number, liked: boolean) => {
    setInteraction(postId, { likeCount: count, liked, error: null });
  }, [setInteraction]);

  /**
   * Comment sayısını manuel güncelle
   */
  const updateCommentCount = useCallback((postId: string, count: number) => {
    setInteraction(postId, { commentCount: count, error: null });
  }, [setInteraction]);

  /**
   * Hataları temizle
   */
  const clearErrors = useCallback((postId?: string) => {
    if (postId) {
      setInteraction(postId, { error: null });
    } else {
      setInteractions(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(id => {
          updated[id] = { ...updated[id], error: null };
        });
        return updated;
      });
    }
  }, [setInteraction]);

  /**
   * Like/Unlike toggle with optimistic updates and retry logic
   * 🛡️ Anti-spam: loading state prevents rapid clicks
   * ✨ Enhanced: Retry on failure, visual feedback
   */
  const toggleLike = useCallback(async (postId: string) => {
    const current = getInteraction(postId);
    
    // 🛡️ KORUMA 1: Zaten loading ise işlem yapma (spam prevention)
    if (current.loading) {
      return;
    }
    
    const wasLiked = current.liked;
    const originalCount = current.likeCount;
    
    // Optimistic update with loading state
    setInteraction(postId, {
      liked: !wasLiked,
      likeCount: wasLiked ? Math.max(0, originalCount - 1) : originalCount + 1,
      loading: true,
      error: null,
    });

    try {
      const likeAction = wasLiked 
        ? () => apiDelete(`/api/likes/${postId}`)
        : () => apiPost(`/api/likes/${postId}`, {});

      // Use retry logic for critical interactions
      await withRetry(likeAction, RetryPresets.CRITICAL);
      
      // Success: Keep optimistic update, clear loading
      setInteraction(postId, { loading: false });
      
    } catch (error: any) {
      // Rollback optimistic update
      
      // 🛡️ Rate limit detection with i18n
      const errorMessage = isRateLimitError(error)
        ? formatRateLimitError(error, 'like')
        : 'Beğeni işlemi başarısız. Tekrar deneyin.';
      
      setInteraction(postId, {
        liked: wasLiked,
        likeCount: originalCount,
        loading: false,
        error: errorMessage,
      });
      
      // Auto-clear error after 3 seconds
      setTimeout(() => {
        setInteraction(postId, { error: null });
      }, 3000);
      
      throw error; // Let caller handle if needed
    }
  }, [getInteraction, setInteraction]);

  /**
   * Birden fazla post için etkileşim durumlarını batch yükle
   * ✨ Enhanced: Parallel loading, better error handling
   */
  const batchLoadInteractions = useCallback(async (postIds: string[]) => {
    if (postIds.length === 0) return;

    // Set loading state for all posts
    postIds.forEach(postId => {
      setInteraction(postId, { loading: true, error: null });
    });

    try {
      // Enhanced batch loading with retry
      const batchLoadWithRetry = async () => {
        const [likeRes, commentRes] = await Promise.all([
          apiPost('/api/likes/state/batch', { postIds }),
          apiPost('/api/comments/count/batch', { postIds }),
        ]);
        return { likeRes, commentRes };
      };

      const { likeRes, commentRes } = await withRetry(batchLoadWithRetry, RetryPresets.STANDARD);

      // Update state with loaded data
      setInteractions(prev => {
        const updated = { ...prev };
        
        // Apply like data
        (likeRes as any)?.posts?.forEach((post: any) => {
          updated[post.id] = {
            ...updated[post.id],
            likeCount: post.count || 0,
            liked: post.liked || false,
            loading: false,
            error: null,
          };
        });

        // Apply comment data
        (commentRes as any)?.counts?.forEach((item: any) => {
          updated[item.postId] = {
            ...updated[item.postId],
            commentCount: item.count || 0,
            loading: false,
            error: null,
          };
        });

        return updated;
      });

    } catch (error: any) {
      // Handle different error types
      if (error?.code === 'not_authenticated' || error?.code === 'session_expired' || error?.code === 'no_auth') {
        // Set default values (0 counts, not liked) when not authenticated
        setInteractions(prev => {
          const updated = { ...prev };
          postIds.forEach(postId => {
            updated[postId] = {
              likeCount: 0,
              commentCount: 0,
              liked: false,
              loading: false,
              error: null,
            };
          });
          return updated;
        });
        return;
      }
      
      // Set default values with error for other failures
      setInteractions(prev => {
        const updated = { ...prev };
        postIds.forEach(postId => {
          updated[postId] = {
            likeCount: 0,
            commentCount: 0,
            liked: false,
            loading: false,
            error: 'Veri yüklenemedi. Sayfayı yenileyin.',
          };
        });
        return updated;
      });

      // Auto-clear errors after 5 seconds
      setTimeout(() => {
        postIds.forEach(postId => {
          setInteraction(postId, { error: null });
        });
      }, 5000);
    }
  }, [setInteraction]);

  return {
    interactions,
    toggleLike,
    updateLikeCount,
    updateCommentCount,
    setInteraction,
    batchLoadInteractions,
    getInteraction,
    clearErrors,
  };
}